import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Loader from "../components/Loader";
import "../assets/clients.css";

const ClientsEmailStatusCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    status_id: "",
    subject: "",
    content: "",
    files: [],
    filesCounter: 1,
  });

  // Available statuses for dropdown
  const [statuses, setStatuses] = useState({});

  // Form errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/clients/emails/status/create`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        const data = await response.json();

        if (data.success && data.data) {
          setStatuses(data.data.statuses || {});
        } else {
          // Fallback to mock data
          setStatuses({
            '': '-- please select --',
            '1': 'Live',
            '2': 'Inactive',
            '3': 'Pending'
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching statuses:", err);
        setStatuses({
          '': '-- please select --',
          '1': 'Live',
          '2': 'Inactive',
          '3': 'Pending'
        });
        setLoading(false);
      }
    };

    fetchStatuses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e, index) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      files: prev.files.map((file, i) => (i === index ? files[0] : file)),
    }));
  };

  const addFileField = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, null],
      filesCounter: prev.filesCounter + 1,
    }));
  };

  const removeFileField = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      filesCounter: prev.filesCounter - 1,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.status_id) {
      newErrors.status_id = "Status is required";
    }

    if (!formData.subject) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.content) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the errors before submitting");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const submitData = {
        status_id: formData.status_id,
        subject: formData.subject,
        content: formData.content,
        filesCounter: formData.filesCounter
      };

      const response = await fetch(`${backendUrl}/api/clients/emails/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}: Failed to send email`);
      }

      setSuccess(data.message || "You have sent the email!");
      setTimeout(() => {
        navigate("/clients");
      }, 2000);
    } catch (err) {
      console.error("Error sending email:", err);
      const errorMessage = err.message || "Failed to send email";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container">
      {/* Breadcrumbs */}
      <div className="breadcrumb-row mb-4">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <Link to="/clients" className="breadcrumb-users">Clients</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-users" style={{ background: '#8bc34a', color: '#fff', padding: '2px 8px', borderRadius: 3 }}>
          Email clients of status
        </span>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-form">
            <div className="panel-heading panel-title">
              Email clients of status
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit} encType="multipart/form-data" id="sendEmailForm">
                <fieldset className="form-horizontal">
                  {/* ID to run JavaScript */}
                  <div id="js-email-attachments"></div>

                  {/* Status Section */}
                  <div className="panel-title title-standard">
                    <h3>Status</h3>
                  </div>
                  <div className="panel-group border-down margin-down">
                    <div className="form-group">
                      <label htmlFor="status_id" className="control-label col-sm-2 label-required">
                        Status
                      </label>
                      <div className="col-sm-3">
                        <select
                          name="status_id"
                          id="status_id"
                          className="form-control"
                          value={formData.status_id}
                          onChange={handleInputChange}
                          required
                        >
                          {Object.entries(statuses).map(([id, name]) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ))}
                        </select>
                        {errors.status_id && (
                          <div className="text-danger">{errors.status_id}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Email Section */}
                  <div className="panel-title title-standard">
                    <h3>Email</h3>
                  </div>
                  <div className="panel-group border-down margin-down">
                    <div className="form-group">
                      <label htmlFor="subject" className="control-label col-sm-2 label-required">
                        Subject
                      </label>
                      <div className="col-sm-10">
                        <input
                          type="text"
                          name="subject"
                          id="subject"
                          className="form-control"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                        />
                        {errors.subject && (
                          <div className="text-danger">{errors.subject}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="content" className="control-label col-sm-2 label-required">
                        Content
                      </label>
                      <div className="col-sm-10">
                        <textarea
                          name="content"
                          id="content"
                          className="form-control ckeditor"
                          value={formData.content}
                          onChange={handleInputChange}
                          rows="10"
                          required
                        />
                        {errors.content && (
                          <div className="text-danger">{errors.content}</div>
                        )}
                      </div>
                    </div>

                    {/* Attachments Section */}
                    <div className="form-group">
                      <label className="control-label col-sm-2">Attachments</label>
                      <div className="col-sm-5">
                        {formData.files.map((file, index) => (
                          <div key={index} style={{ marginBottom: '10px' }}>
                            <input
                              type="file"
                              onChange={(e) => handleFileChange(e, index)}
                              className="form-control"
                            />
                            {formData.files.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFileField(index)}
                                className="btn btn-sm btn-danger"
                                style={{ marginTop: '5px' }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addFileField}
                          className="btn btn-sm btn-default"
                        >
                          Add File
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="form-horizontal">
                  <div className="form-group text-right">
                    <div className="col-sm-12">
                      {error && (
                        <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="alert alert-success" style={{ marginBottom: '15px' }}>
                          {success}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="btn btn-head btn-standard prevent-multiple-requests"
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send email"}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsEmailStatusCreate;

